import { type ReactNode, useEffect } from "react";
import { Link } from "react-router-dom";
import type { PageSize } from "../api/types";

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

export function Breadcrumbs({ items }: { items: BreadcrumbItem[] }) {
  return <nav className="breadcrumbs" aria-label="面包屑">{items.map((item, index) => <span key={`${item.label}-${index}`}>
    {index > 0 && <span className="breadcrumb-separator" aria-hidden="true">/</span>}
    {item.to ? <Link to={item.to}>{item.label}</Link> : <span aria-current="page">{item.label}</span>}
  </span>)}</nav>;
}

export function Modal({ title, open, onClose, children, width = "medium" }: {
  title: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  width?: "small" | "medium" | "large";
}) {
  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [open, onClose]);
  if (!open) return null;
  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className={`modal modal-${width}`} role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <header className="modal-header"><h2 id="modal-title">{title}</h2><button className="icon-button" type="button" aria-label="关闭" onClick={onClose}>×</button></header>
      <div className="modal-body">{children}</div>
    </section>
  </div>;
}

export function ListToolbar({ query, onQueryChange, placeholder, count, children, sort, onSortChange }: {
  query: string;
  onQueryChange: (value: string) => void;
  placeholder: string;
  count: number;
  children?: ReactNode;
  sort?: string;
  onSortChange?: (value: string) => void;
}) {
  return <div className="list-toolbar">
    <label className="search cvat-search"><span aria-hidden="true">⌕</span><input aria-label="搜索" type="search" placeholder={placeholder} value={query} onChange={(event) => onQueryChange(event.target.value)} /></label>
    {children}
    {onSortChange && <label className="compact-field"><span>排序</span><select aria-label="排序" value={sort} onChange={(event) => onSortChange(event.target.value)}><option value="created-desc">最新创建</option><option value="name-asc">名称 A–Z</option><option value="name-desc">名称 Z–A</option></select></label>}
    <span className="record-count">{count} 条</span>
  </div>;
}

export function Pagination({ page, pageSize, total, onPageChange, onPageSizeChange }: {
  page: number;
  pageSize: PageSize;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: PageSize) => void;
}) {
  if (total === 0) return null;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pages);
  const visiblePages = pageNumbers(safePage, pages);
  return <nav className="pagination" aria-label="分页">
    <span className="pagination-summary">第 {safePage} / {pages} 页，共 {total} 条</span>
    <div className="pagination-pages">
      <button className="button ghost compact" type="button" disabled={safePage <= 1} onClick={() => onPageChange(safePage - 1)}>上一页</button>
      {visiblePages.map((item, index) => item === "…"
        ? <span className="pagination-ellipsis" key={`ellipsis-${index}`} aria-hidden="true">…</span>
        : <button className="pagination-number" type="button" key={item} aria-current={item === safePage ? "page" : undefined} onClick={() => onPageChange(item)}>{item}</button>)}
      <button className="button ghost compact" type="button" disabled={safePage >= pages} onClick={() => onPageChange(safePage + 1)}>下一页</button>
    </div>
    <label className="compact-field pagination-size"><span>每页</span><select aria-label="每页数量" value={pageSize} onChange={(event) => onPageSizeChange(Number(event.target.value) as PageSize)}><option value={20}>20 条</option><option value={50}>50 条</option></select></label>
  </nav>;
}

export function JobListHeader({ primaryLabel = "任务 / 工作" }: { primaryLabel?: string }) {
  return <div className="job-list-header" aria-label="工作列表列标题">
    <span>{primaryLabel}</span>
    <span>负责人</span>
    <span>状态</span>
    <span>版本</span>
    <span>操作</span>
  </div>;
}

function pageNumbers(page: number, pages: number): Array<number | "…"> {
  if (pages <= 7) return Array.from({ length: pages }, (_, index) => index + 1);
  const values = new Set([1, pages, page - 1, page, page + 1]);
  const numbers = [...values].filter((item) => item >= 1 && item <= pages).sort((left, right) => left - right);
  const result: Array<number | "…"> = [];
  numbers.forEach((item, index) => {
    if (index > 0 && item - numbers[index - 1] > 1) result.push("…");
    result.push(item);
  });
  return result;
}

export function WorkflowEmpty({ title, detail, action }: { title: string; detail: string; action?: ReactNode }) {
  return <div className="workflow-empty"><span className="empty-icon" aria-hidden="true">◎</span><strong>{title}</strong><p>{detail}</p>{action}</div>;
}

export function PermissionHint({ children }: { children: ReactNode }) {
  return <div className="permission-hint" role="note"><span aria-hidden="true">i</span><div>{children}</div></div>;
}

export function Metric({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return <div className="metric"><span>{label}</span><strong>{value}</strong>{hint && <small>{hint}</small>}</div>;
}
